/**
 * App Factories - Zero-Config App Creation
 *
 * Abstracts ALL infrastructure (auth, providers, routing, bundle mode, SDK)
 * so developers can focus purely on business logic.
 *
 * Entry Point Pattern:
 * - main.tsx: Mounts app (framework concern)
 * - App.tsx: Business logic ONLY (developer concern)
 * - Infrastructure: Hidden in factories (SDK concern)
 *
 * Usage:
 * ```tsx
 * // apps/myapp/src/App.tsx (Simple single-page app)
 * import { createSimpleApp } from '@room/shared-ui';
 * import HomePage from './pages/HomePage';
 *
 * export const App = createSimpleApp({
 *   name: 'MyApp',
 *   component: HomePage,
 * });
 * ```
 *
 * ```tsx
 * // apps/admin/src/App.tsx (Routed app with sidebar)
 * import { createRoutedApp } from '@room/shared-ui';
 * import { routes } from './routes';
 * import { sidebar } from './navigation';
 *
 * export const App = createRoutedApp({
 *   name: 'Admin',
 *   basename: '/admin',
 *   routes,
 *   sidebar,
 * });
 * ```
 */

import { ComponentType, ReactElement, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, RouteObject } from 'react-router-dom';
import { RootLayout, type RootLayoutProps } from '../layouts/RootLayout';
import { AppShell, type SidebarNavItem } from '../layouts/AppShell';
import { isBundleMode } from '../utils/bundleMode';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Simple single-page app configuration
 * Use for apps without routing (Chat, Store, Home)
 */
export interface SimpleAppConfig {
  /**
   * Application name (displayed in loading screens)
   */
  name: string;

  /**
   * Root component to render
   */
  component: ComponentType;

  /**
   * Require authentication
   * @default true
   */
  requireAuth?: boolean;

  /**
   * Additional content to render alongside main component
   * Example: <DevConsole />, <UpdateNotification />
   */
  additionalContent?: ReactNode;

  /**
   * Custom loading/redirect messages
   */
  loadingMessage?: string;
  redirectMessage?: string;

  /**
   * Advanced: Custom RootLayout props
   */
  rootLayoutProps?: Partial<RootLayoutProps>;
}

/**
 * Route definition for routed apps
 * Simplified version of React Router's RouteObject
 */
export interface AppRoute {
  /**
   * Route path (e.g., '/', '/settings', '/users/:id')
   */
  path: string;

  /**
   * Component to render for this route
   */
  component: ComponentType;

  /**
   * Child/nested routes
   */
  children?: AppRoute[];
}

/**
 * Routed app with sidebar navigation
 * Use for apps with multiple sections (Admin, Developer)
 */
export interface RoutedAppConfig {
  /**
   * Application name
   */
  name: string;

  /**
   * Base path for router (e.g., '/admin', '/developer')
   * @default '/'
   */
  basename?: string;

  /**
   * Array of route definitions
   */
  routes: AppRoute[];

  /**
   * Sidebar navigation items (optional)
   * If not provided, app will render without sidebar
   */
  sidebar?: SidebarNavItem[];

  /**
   * Default route to redirect to
   * @default first route in routes array
   */
  defaultRoute?: string;

  /**
   * Show sidebar in bundle mode
   * @default true
   */
  showSidebarInBundle?: boolean;

  /**
   * Show header in bundle mode
   * @default true
   */
  showHeaderInBundle?: boolean;

  /**
   * Show bottom navigation in bundle mode
   * @default true
   */
  showBottomNavInBundle?: boolean;

  /**
   * Map of route paths to display titles
   * Used for header title if not set dynamically
   */
  routeTitles?: Record<string, string>;

  /**
   * Require authentication
   * @default true
   */
  requireAuth?: boolean;

  /**
   * Additional content to render (e.g., DevConsole)
   */
  additionalContent?: ReactNode;

  /**
   * Advanced: Custom RootLayout props
   */
  rootLayoutProps?: Partial<RootLayoutProps>;
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Create a simple single-page app
 *
 * Automatically handles:
 * - Authentication flow
 * - QueryClient provider
 * - Error boundaries
 * - Toast/notification providers
 * - Bundle mode detection
 * - Loading screens
 *
 * Developer only needs to provide:
 * - App name
 * - Root component
 *
 * @example
 * ```tsx
 * // apps/store/src/App.tsx
 * import { createSimpleApp } from '@room/shared-ui';
 * import StorePage from './pages/Store';
 *
 * export const App = createSimpleApp({
 *   name: 'Store',
 *   component: StorePage,
 * });
 * ```
 */
export function createSimpleApp(config: SimpleAppConfig): () => ReactElement {
  const {
    name,
    component: Component,
    requireAuth = true,
    additionalContent,
    loadingMessage,
    redirectMessage,
    rootLayoutProps,
  } = config;

  return function App() {
    return (
      <RootLayout
        appName={name}
        requireAuth={requireAuth}
        loadingMessage={loadingMessage}
        redirectMessage={redirectMessage}
        {...rootLayoutProps}
      >
        <Component />
        {additionalContent}
      </RootLayout>
    );
  };
}

/**
 * Create a routed app with optional sidebar navigation
 *
 * Automatically handles:
 * - Everything from createSimpleApp
 * - React Router setup with basename
 * - Sidebar navigation with active state
 * - Header management via context
 * - Responsive mobile behavior
 * - Default route redirects
 * - Catch-all 404 handling
 *
 * Developer only needs to provide:
 * - App name
 * - Routes array
 * - Optional sidebar items
 *
 * @example
 * ```tsx
 * // apps/admin/src/App.tsx
 * import { createRoutedApp } from '@room/shared-ui';
 *
 * export const App = createRoutedApp({
 *   name: 'Admin',
 *   basename: '/admin',
 *   routes: [
 *     { path: '/pipeline', component: PipelinePage },
 *     { path: '/settings', component: SettingsPage },
 *   ],
 *   sidebar: [
 *     { id: 'pipeline', label: 'Pipeline', icon: '📈', path: '/pipeline' },
 *     { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings' },
 *   ],
 *   defaultRoute: '/pipeline',
 * });
 * ```
 */
export function createRoutedApp(config: RoutedAppConfig): () => ReactElement {
  const {
    name,
    basename = '/',
    routes,
    sidebar = [],
    defaultRoute,
    showSidebarInBundle = true,
    showHeaderInBundle = true,
    showBottomNavInBundle = true,
    routeTitles = {},
    requireAuth = true,
    additionalContent,
    rootLayoutProps,
  } = config;

  // Determine default route
  const resolvedDefaultRoute = defaultRoute || (routes.length > 0 ? routes[0].path : '/');

  // Convert AppRoute[] to React Router Route elements
  const renderRoutes = (routeList: AppRoute[]): ReactElement[] => {
    return routeList.map((route) => {
      const Component = route.component;
      return (
        <Route
          key={route.path}
          path={route.path}
          element={<Component />}
        >
          {route.children && renderRoutes(route.children)}
        </Route>
      );
    });
  };

  return function App() {
    // In bundle mode, disable basename since app is rendered in overlay container
    // The URL doesn't actually change when bundle apps are loaded
    const effectiveBasename = isBundleMode() ? undefined : basename;

    console.log(`[createRoutedApp ${name}] Using basename:`, effectiveBasename, '(bundle mode:', isBundleMode(), ')');

    // Check if we need a root redirect
    // Don't add redirect if resolvedDefaultRoute is already '/' - it would conflict
    const needsRootRedirect = resolvedDefaultRoute !== '/';

    return (
      <RootLayout
        appName={name}
        requireAuth={requireAuth}
        {...rootLayoutProps}
      >
        <BrowserRouter basename={effectiveBasename}>
          {sidebar.length > 0 ? (
            // Routed app with sidebar
            <AppShell
              appName={name}
              sidebarItems={sidebar}
              routeTitles={routeTitles}
              showSidebarInBundle={showSidebarInBundle}
              showHeaderInBundle={showHeaderInBundle}
              showBottomNavInBundle={showBottomNavInBundle}
            >
              <Routes>
                {/* Default redirect - only if default route is NOT '/' */}
                {needsRootRedirect && (
                  <Route path="/" element={<Navigate to={resolvedDefaultRoute} replace />} />
                )}

                {/* App routes */}
                {renderRoutes(routes)}

                {/* Catch-all 404 redirect */}
                <Route path="*" element={<Navigate to={resolvedDefaultRoute} replace />} />
              </Routes>
            </AppShell>
          ) : (
            // Routed app without sidebar (minimal)
            <Routes>
              {needsRootRedirect && (
                <Route path="/" element={<Navigate to={resolvedDefaultRoute} replace />} />
              )}
              {renderRoutes(routes)}
              <Route path="*" element={<Navigate to={resolvedDefaultRoute} replace />} />
            </Routes>
          )}
        </BrowserRouter>
        {additionalContent}
      </RootLayout>
    );
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert simple routes config to sidebar items
 * Useful for quick prototyping
 */
export function routesToSidebar(routes: AppRoute[], icons?: Record<string, string>): SidebarNavItem[] {
  return routes.map((route) => ({
    id: route.path.replace('/', '') || 'home',
    label: route.path === '/' ? 'Home' : route.path.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    path: route.path,
    icon: icons?.[route.path] || '📄',
  }));
}
